import { mkdir, open } from "node:fs/promises";
import { dirname, join } from "node:path";
import type { ModuleContext } from "@vortex-os/core";
import { WorklogStore } from "@vortex-os/worklog";

/**
 * Ensure a dated worklog entry exists — the host-side **create** primitive
 * the worklog module deliberately leaves out (`WorklogStore` reads; `/log`
 * only appends and errors when today's entry is missing). Auto-worklog
 * (the agent at wind-down, or the SessionEnd net) needs *creation*, so it
 * lives here, where instance frontmatter conventions belong.
 *
 * Idempotent: if a worklog already exists for the date (with any keyword), it
 * is returned untouched (`created: false`) — never overwritten. Pair with
 * `appendSection` from `@vortex-os/worklog` to add the session's content.
 */
export interface EnsureWorklogResult {
  readonly path: string;
  readonly date: string;
  readonly keyword: string;
  /** True when this call created the file; false when one already existed. */
  readonly created: boolean;
}

export async function ensureWorklogEntry(
  ctx: ModuleContext,
  opts?: {
    readonly now?: Date;
    readonly keyword?: string;
    readonly title?: string;
    readonly body?: string;
  },
): Promise<EnsureWorklogResult> {
  const now = opts?.now ?? new Date();
  const date = isoDate(now);
  const time = isoTime(now);
  const store = new WorklogStore(join(ctx.dataDir, "worklog"));

  // Any existing entry for the date (any keyword) counts — never duplicate a day.
  const existing = await store.get(date);
  if (existing) {
    return { path: existing.path, date: existing.date, keyword: existing.keyword, created: false };
  }

  // CREATE path — derive a TOPIC keyword; never silently fall back to the placeholder
  // "worklog" (a generic `…-worklog.md` name breaks the `YYYY-MM-DD_HHMM-<topic>.md`
  // convention and recurred as a real miss, 2026-06). Resolve in order: an explicit
  // non-generic keyword → a slug of a real title → a slug of the body's first line
  // (so the auto-net "body only" create still gets a topic, not "worklog"). Only a
  // truly info-less call (no keyword, no title, no body) is REFUSED.
  const explicit = (opts?.keyword ?? "").trim();
  const explicitSlug = explicit && explicit.toLowerCase() !== "worklog" ? slugifyKeyword(explicit) : "";
  const fromTitle = opts?.title && opts.title !== `${date} worklog` ? slugifyKeyword(opts.title) : "";
  const fromBody = firstTopicFromBody(opts?.body);
  const keyword = explicitSlug || fromTitle || (fromBody.toLowerCase() !== "worklog" ? fromBody : "");
  if (!keyword) {
    throw new Error(
      "ensureWorklogEntry: a TOPIC keyword is required — refusing to create a generic " +
        "`…-worklog.md` file. Pass opts.keyword (the work's topic), a descriptive opts.title, or body content.",
    );
  }

  const path = store.pathFor(date, keyword, time);
  const title = opts?.title ?? `${date} worklog`;
  await mkdir(dirname(path), { recursive: true });
  // EXCLUSIVE create (`wx`) — the get()/write above is check-then-act, so two concurrent
  // sessions (supported) writing the same date+keyword+minute would otherwise silently
  // clobber each other (data loss). Matches the handoff/failures create-primitives. On a
  // same-path collision, re-check the day and return the existing entry instead of overwriting.
  try {
    const fh = await open(path, "wx");
    try {
      await fh.writeFile(renderWorklogFile(date, title, opts?.body ?? ""), "utf8");
    } finally {
      await fh.close();
    }
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "EEXIST") {
      const raced = await store.get(date);
      if (raced) return { path: raced.path, date: raced.date, keyword: raced.keyword, created: false };
      return { path, date, keyword, created: false };
    }
    throw e;
  }
  return { path, date, keyword, created: true };
}

function renderWorklogFile(date: string, title: string, body: string): string {
  const trimmed = body.trimEnd();
  return (
    `---\n` +
    `type: worklog\n` +
    `created: ${date}\n` +
    `updated: ${date}\n` +
    `tags: [worklog]\n` +
    `---\n\n` +
    `# ${title}\n` +
    (trimmed ? `\n${trimmed}\n` : ``)
  );
}

/**
 * Slugify a topic into a filename keyword: keep unicode letters/numbers (so Korean
 * topics survive), collapse the rest to single dashes, drop path separators, cap length.
 * Returns "" for an input with no usable characters (the caller treats that as "no
 * keyword" and falls back / refuses, rather than writing a junk name).
 */
function slugifyKeyword(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .replace(/[\\/]+/g, "-") // path separators → dash
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}_-]+/gu, "") // keep letters/numbers (any script) + _ -
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, ""); // tidy a dash left at the truncation boundary
}

/** First non-empty body line (heading/list markers stripped) → a keyword slug, or "". */
function firstTopicFromBody(body?: string): string {
  if (!body) return "";
  for (const raw of body.split(/\r?\n/)) {
    const line = raw
      .replace(/^#{1,6}\s*/, "") // markdown heading marker
      .replace(/^[-*+]\s+/, "") // list bullet
      .trim();
    if (line) return slugifyKeyword(line);
  }
  return "";
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoTime(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}${m}`;
}
